import React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { formatKMBWithPlaceholder } from '@intelligence/v3/utils/number';

import type { IMapChartTooltipProps, IMapTooltipDataItem, ICreateMapChartTooltipFormatterParams } from '../types';

/**
 * 地图 Tooltip 组件
 */
const MapChartTooltip: React.FC<IMapChartTooltipProps> = (props) => {
  const { title, dataList } = props;

  return (
    <div className="v3-chart-tooltip v3-map-tooltip">
      {/* 标题区域：国家/地区名称 */}
      <div className="v3-tooltip-title">
        <div className="v3-tooltip-title-text">{title}</div>
      </div>

      {/* 数据列表 */}
      <div className="v3-tooltip-list">
        {dataList.map((item, index) => (
          <div className="v3-tooltip-list-item" key={index}>
            <div className="v3-tooltip-list-item-label">{item.label}</div>
            <div className="v3-tooltip-list-item-value">{item.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * 渲染地图 Tooltip 为 HTML 字符串
 */
export const renderMapChartTooltip = (props: IMapChartTooltipProps): string => {
  return renderToStaticMarkup(<MapChartTooltip {...props} />);
};

/**
 * 创建地图 Tooltip formatter
 * @description 基于传入的 formatters 生成 ECharts tooltipFormatter 函数
 */
export const createMapChartTooltipFormatter = (
  params: ICreateMapChartTooltipFormatterParams,
): ((echartsParams: any) => string) => {
  const { metricName, countryNameFormatter, valueFormatter = formatKMBWithPlaceholder, extraDataItems = [] } = params;

  return (echartsParams: any): string => {
    // 获取国家/地区名称
    // 优先从 payload.market 获取国家代码，然后通过 formatter 翻译
    // 如果没有 payload.market，则使用 name
    let displayName = '';
    const marketCode = echartsParams?.data?.payload?.market;
    const nameFromData = echartsParams?.data?.name || echartsParams?.name || '';

    if (marketCode && countryNameFormatter) {
      displayName = countryNameFormatter(marketCode);
    } else if (countryNameFormatter) {
      displayName = countryNameFormatter(nameFromData);
    } else {
      displayName = nameFromData;
    }

    // 获取指标值
    const value = echartsParams?.data?.value ?? echartsParams?.value;
    const payload = echartsParams?.data?.payload || {};

    // 构建数据列表
    const dataList: IMapTooltipDataItem[] = [
      {
        label: metricName,
        value: valueFormatter(value),
        rawValue: typeof value === 'number' ? value : null,
      },
    ];

    // 添加额外数据项
    extraDataItems.forEach((extraItem) => {
      const extraValue = payload[extraItem.payloadKey];
      if (extraValue !== undefined && extraValue !== null) {
        dataList.push({
          label: extraItem.label,
          value: extraItem.valueFormatter ? extraItem.valueFormatter(extraValue) : String(extraValue),
          rawValue: typeof extraValue === 'number' ? extraValue : null,
        });
      }
    });

    return renderMapChartTooltip({
      title: displayName,
      dataList,
    });
  };
};

export default MapChartTooltip;
